import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { MapPin } from "lucide-react";

export function AdminCities() {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ name: "", state: "Andhra Pradesh" });
  const load = () => api.get("/cities").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!f.name) return toast.error("Name required");
    try { await api.post("/admin/cities", f); toast.success("City added"); setF({ name: "", state: "Andhra Pradesh" }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const toggle = async (c) => {
    await api.put(`/admin/cities/${c.id}`, { active: !c.active });
    load();
  };
  return (
    <div>
      <h1 className="font-display text-3xl font-bold">Cities</h1>
      <p className="text-sm text-stone-500 mt-1">Chapters where CGC operates. Only active cities appear in filters.</p>
      <div className="mt-6 cgc-card p-5 flex flex-col sm:flex-row gap-3">
        <Input placeholder="City name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="new-city-name" />
        <Input placeholder="State" value={f.state} onChange={(e) => setF({ ...f, state: e.target.value })} data-testid="new-city-state" />
        <Button className="btn-brand" onClick={create} data-testid="new-city-submit">Add</Button>
      </div>
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        {items.map((c) => (
          <div key={c.id} className="cgc-card p-4 flex items-center justify-between" data-testid={`city-${c.id}`}>
            <div>
              <div className="font-display text-lg font-semibold">{c.name}</div>
              <div className="text-xs text-stone-500">{c.state}</div>
            </div>
            <Button size="sm" variant="outline" onClick={() => toggle(c)}>{c.active ? "Deactivate" : "Activate"}</Button>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AdminVenues() {
  const [items, setItems] = useState([]);
  const [cities, setCities] = useState([]);
  const empty = { name: "", address: "", city: "Visakhapatnam", capacity: "", latitude: "", longitude: "", google_maps_url: "", notes: "", photos: [] };
  const [f, setF] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [newPhoto, setNewPhoto] = useState("");
  const load = () => api.get("/venues").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/cities").then((r) => setCities(r.data)); }, []);
  const save = async () => {
    if (!f.name) return toast.error("Name required");
    const payload = {
      ...f,
      capacity: f.capacity ? parseInt(f.capacity) : null,
      latitude: f.latitude ? parseFloat(f.latitude) : null,
      longitude: f.longitude ? parseFloat(f.longitude) : null,
      photos: f.photos || [],
    };
    if (!payload.google_maps_url && payload.latitude && payload.longitude) {
      payload.google_maps_url = `https://www.google.com/maps/search/?api=1&query=${payload.latitude},${payload.longitude}`;
    }
    try {
      if (editing) { await api.put(`/admin/venues/${editing}`, payload); toast.success("Venue updated"); }
      else { await api.post("/admin/venues", payload); toast.success("Venue added"); }
      setF(empty); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const edit = (v) => { setEditing(v.id); setF({
    name: v.name || "", address: v.address || "", city: v.city || "", capacity: v.capacity || "",
    latitude: v.latitude || "", longitude: v.longitude || "", google_maps_url: v.google_maps_url || "", notes: v.notes || "",
    photos: v.photos || [],
  }); window.scrollTo(0, 0); };
  return (
    <div>
      <h1 className="font-display text-3xl font-bold">Venues</h1>
      <p className="text-sm text-stone-500 mt-1">Save reusable venues with coordinates and Google Maps link. New events auto-fill from these.</p>

      <div className="mt-6 cgc-card p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Name *" testid="venue-name" value={f.name} onChange={(v) => setF({ ...f, name: v })} />
        <div>
          <label className="text-xs">City</label>
          <select className="mt-1 w-full border border-border rounded-md px-3 py-2 text-sm bg-background" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} data-testid="venue-city">
            {cities.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
          </select>
        </div>
        <Field label="Address" testid="venue-address" value={f.address} onChange={(v) => setF({ ...f, address: v })} />
        <Field label="Capacity" testid="venue-cap" value={f.capacity} onChange={(v) => setF({ ...f, capacity: v })} type="number" />
        <Field label="Latitude" testid="venue-lat" value={f.latitude} onChange={(v) => setF({ ...f, latitude: v })} />
        <Field label="Longitude" testid="venue-lng" value={f.longitude} onChange={(v) => setF({ ...f, longitude: v })} />
        <div className="md:col-span-2">
          <Field label="Google Maps URL (auto-filled from lat/lng if empty)" testid="venue-maps" value={f.google_maps_url} onChange={(v) => setF({ ...f, google_maps_url: v })} />
        </div>
        <div className="md:col-span-2">
          <label className="text-xs">Photos (paste image URLs — appear on event page carousel)</label>
          <div className="flex gap-2 mt-1">
            <Input placeholder="https://images.example.com/venue.jpg" value={newPhoto} onChange={(e) => setNewPhoto(e.target.value)} data-testid="venue-photo-input" />
            <Button variant="outline" onClick={() => { if (newPhoto) { setF({ ...f, photos: [...(f.photos || []), newPhoto] }); setNewPhoto(""); } }} data-testid="venue-photo-add">Add</Button>
          </div>
          {(f.photos || []).length > 0 && (
            <div className="mt-3 grid grid-cols-3 md:grid-cols-6 gap-2">
              {(f.photos || []).map((p, i) => (
                <div key={i} className="relative" data-testid={`venue-photo-${i}`}>
                  <img src={p} alt="" className="w-full h-20 object-cover rounded-md border border-border" />
                  <button className="absolute top-1 right-1 bg-red-600 text-white rounded p-1" onClick={() => setF({ ...f, photos: (f.photos || []).filter((_, x) => x !== i) })}>×</button>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="md:col-span-2 flex gap-2 justify-end">
          {editing && <Button variant="outline" onClick={() => { setEditing(null); setF(empty); }}>Cancel</Button>}
          <Button className="btn-brand" onClick={save} data-testid="venue-save">{editing ? "Update venue" : "Add venue"}</Button>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((v) => (
          <div key={v.id} className="cgc-card p-5" data-testid={`venue-${v.id}`}>
            <div className="flex justify-between items-start gap-3">
              <div>
                <div className="font-display text-lg font-semibold">{v.name}</div>
                <div className="text-sm text-stone-500">{v.address}, {v.city}</div>
                <div className="text-xs mt-1 flex flex-wrap gap-3 text-stone-500">
                  {v.capacity && <span>Cap: {v.capacity}</span>}
                  {v.latitude && <span>{v.latitude}, {v.longitude}</span>}
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <Button size="sm" variant="outline" onClick={() => edit(v)}>Edit</Button>
                {v.google_maps_url && <a href={v.google_maps_url} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="w-full"><MapPin size={12} className="mr-1" />Map</Button></a>}
              </div>
            </div>
            {v.latitude && v.longitude && (
              <div className="mt-3 rounded-md overflow-hidden border border-border">
                <iframe title={`map-${v.id}`} src={`https://www.google.com/maps?q=${v.latitude},${v.longitude}&z=14&output=embed`} width="100%" height="180" loading="lazy" style={{ border: 0 }} />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export function AdminQuotes() {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ quote: "", author: "", role: "" });
  const load = () => api.get("/quotes").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!f.quote || !f.author) return toast.error("Quote and author are required");
    try { await api.post("/admin/quotes", f); toast.success("Added"); setF({ quote: "", author: "", role: "" }); load(); }
    catch (e) { toast.error("Failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this quote?")) return;
    await api.delete(`/admin/quotes/${id}`); load();
  };
  return (
    <div>
      <h1 className="font-display text-3xl font-bold">Beneficiary quotes</h1>
      <p className="text-sm text-stone-500 mt-1">These appear on the public /impact page.</p>
      <div className="mt-6 cgc-card p-5 space-y-3">
        <textarea className="w-full border border-border rounded-md px-3 py-2 text-sm bg-background" rows={3} placeholder="Quote" value={f.quote} onChange={(e) => setF({ ...f, quote: e.target.value })} data-testid="quote-text" />
        <div className="grid grid-cols-2 gap-3">
          <Input placeholder="Author name" value={f.author} onChange={(e) => setF({ ...f, author: e.target.value })} data-testid="quote-author" />
          <Input placeholder="Role (optional)" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} data-testid="quote-role" />
        </div>
        <Button className="btn-brand" onClick={create} data-testid="quote-submit">Publish quote</Button>
      </div>
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((q) => (
          <div key={q.id} className="cgc-card p-5" data-testid={`quote-item-${q.id}`}>
            <p className="italic font-display">&ldquo;{q.quote}&rdquo;</p>
            <div className="mt-3 flex items-center justify-between">
              <div className="text-sm"><b>{q.author}</b>{q.role && <span className="text-stone-500"> — {q.role}</span>}</div>
              <Button size="sm" variant="outline" onClick={() => del(q.id)}>Delete</Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Field({ label, testid, value, onChange, type = "text" }) {
  return (
    <div>
      <label className="text-xs">{label}</label>
      <Input type={type} data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="mt-1" />
    </div>
  );
}
